import * as React from 'react';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { Link } from 'react-router';

import { cn } from '../../utils/cn';

const paginationLinkVariants = cva(
    'inline-flex size-11.5 items-center justify-center whitespace-nowrap rounded-full text-base font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
    {
        variants: {
            variant: {
                outline: 'border border-input bg-gray-900 text-gray-50',
                ghost: 'md:hover:bg-[#d8d8d8]',
                disabled: 'cursor-not-allowed opacity-15',
            },
        },
        defaultVariants: {
            variant: 'ghost',
        },
    },
);

type PaginationLinkProps = React.ComponentProps<typeof Link> &
    VariantProps<typeof paginationLinkVariants> & {
        isActive?: boolean;
        isDisabled?: boolean; // Added support for disabling link
    };

export function PaginationLink({
    className,
    isActive,
    isDisabled,
    ...props
}: PaginationLinkProps) {
    if (isDisabled) {
        return (
            <span
                data-slot="pagination-link"
                className={cn(
                    paginationLinkVariants({ variant: 'disabled' }),
                    className,
                )}
                aria-disabled
            >
                {props.children}
            </span>
        );
    }

    return (
        <Link
            aria-current={isActive ? 'page' : undefined}
            data-slot="pagination-link"
            data-active={isActive}
            className={cn(
                paginationLinkVariants({
                    variant: isActive ? 'outline' : 'ghost',
                }),
                className,
            )}
            {...props}
        />
    );
}
